import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import gsap from 'gsap';
import { times } from 'lodash';
import ScrollToPlugin from 'gsap/dist/ScrollToPlugin';

import RegisterBox from '../RegisterBox';
import styles from './index.module.scss';
import { useComputedTabWidth, useComputedLeftOffset } from './context';
import { TAB_NUM, KR_TAB_NUM, ZA_TAB_NUM, tabsContent, TAB_HEIGHT, TAB_MARGIN } from './constant';
import { useLang } from '@/i18n';
import animate from '@/utils/Animate';

gsap.registerPlugin(ScrollToPlugin);

export default function AnchorTabs() {
    let controller: any;

    const { contentRef, chooseTabRef, tabWidths } = useComputedTabWidth();
    const { t } = useLang('ecocreditsday');
    const { locale } = useRouter();
    const tabContent = useRef(null);
    const { offsetItems } = useComputedLeftOffset(tabWidths, contentRef);

    const [activeTab, setActiveTab] = useState('tab_content_0');
    const tabNum = {
        jp: TAB_NUM,
        kr: KR_TAB_NUM,
        za: ZA_TAB_NUM,
        au: 3,
    };
    const tabLen = tabNum[locale as keyof typeof locale] || tabNum.za;

    const tabs = times(tabLen, (i) => ({ key: `tab_content_${i}`, name: t(`pageTabs.tab${i + 2}`) }));
    const tabsContentArr: [any] = tabsContent[locale as keyof typeof locale] || tabsContent.za;

    const handleTabChange = (key: string) => {
        const currentTab = document.querySelector(`#${key}`);

        setActiveTab(key);
        gsap.to(document.documentElement, { duration: 2, scrollTo: { y: currentTab?.offsetTop - TAB_HEIGHT } });
    };

    useEffect(() => {
        controller = new ScrollMagic.Controller();
    }, []);

    useEffect(() => {
        const toIndex = tabsContentArr.findIndex((item: any) => item.id === activeTab);
        // 偏移动画
        if (contentRef.current && toIndex >= 0) {
            const start = contentRef.current.scrollLeft;
            const end = offsetItems.current[toIndex];
            const callback = (value: number) => ((contentRef.current as HTMLElement).scrollLeft = !toIndex ? value : value + TAB_MARGIN * toIndex);
            animate({ start, end, callback });
        }
    }, [activeTab]);

    // 滚动 Tab 切换动画
    useEffect(() => {
        tabsContentArr.forEach((item: any, index: number) => {
            const scene = new ScrollMagic.Scene({ triggerElement: `#${item.id}`, triggerHook: 'onEnter', offset: window.innerHeight / 2 });

            scene.on('enter', () => {
                setActiveTab(item.id);
            });
            scene.on('leave', () => {
                const preEnter = index - 1;
                if (preEnter >= 0) setActiveTab(tabsContentArr[preEnter]['id']);
            });
            controller.addScene(scene);
        });
    }, []);

    return (
        <div className={styles.anchorTabs}>
            <div className="tabs-box">
                <div className="menu-box" ref={contentRef}>
                    <div className="tabs-cover" />
                    <div className="tabs-menu" ref={chooseTabRef}>
                        {tabs.map((item: Record<string, string>) => (
                            <div
                                key={item.key}
                                title="tab"
                                className={`tab-item ${item.key === activeTab ? 'active-tab' : ''}`}
                                onClick={() => handleTabChange(item.key)}
                            >
                                {item.name}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="content-box" ref={tabContent}>
                    {tabsContentArr?.map((contentItem: any) => (
                        <div key={contentItem.id} className="tab-content" id={contentItem.id}>
                            {contentItem.element}
                        </div>
                    ))}
                    {!['jp', 'kr'].includes(locale as string) && <RegisterBox />}
                </div>
            </div>
        </div>
    );
}
